"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AiRunRowSchema = exports.AI_RUN_SOURCE_MAX = exports.AiRunSourceSchema = exports.AiRunParamsSchema = exports.AiRunAttemptSchema = exports.AiRunTargetTypeSchema = exports.AI_RUN_TARGET_TYPES = exports.AiRunSettlementSchema = exports.AI_RUN_SETTLEMENTS = exports.AiRunStatusSchema = exports.AI_RUN_STATUSES = void 0;
const zod_1 = require("zod");
/**
 * RUN# rows — the Ruby Generation Engine's ledger of one generation request.
 *
 * Rows live in the tenant's AI table under pk=`TENANT#<tenantId>`,
 * sk=`RUN#<runId>`. One row tracks a run end to end: reservation, every
 * vendor attempt (including fallbacks), settlement and the final output or
 * error. `attempts` is append-only; everything else updates in place as the
 * run progresses through `status`.
 *
 * See docs/.superpowers/sdd/2026-10-04-ruby-generation-engine.
 */
exports.AI_RUN_STATUSES = ['queued', 'generating', 'attaching', 'succeeded', 'failed', 'cancelled'];
exports.AiRunStatusSchema = zod_1.z.enum(exports.AI_RUN_STATUSES);
exports.AI_RUN_SETTLEMENTS = ['none', 'reserved', 'committed', 'refunded'];
exports.AiRunSettlementSchema = zod_1.z.enum(exports.AI_RUN_SETTLEMENTS);
/** `composition` (Task 27): a media-service menu board / flyer composition job row. */
exports.AI_RUN_TARGET_TYPES = [
    'unit', 'category', 'ingredient', 'supply', 'recipe', 'preview', 'product',
    'banner', 'creator_content', 'routine_cover', 'video', 'composition',
    // Website builder pictures (hero backgrounds, story images): wide, full-bleed.
    'site_image',
    // Careers job adverts (2026-10-08): 4:5 portrait hiring picture, preview semantics.
    'job_ad',
    // Ruby Social Publishing (2026-10-13): per-channel picture (4:3 / 1:1 / 4:5), preview semantics.
    'social_post',
];
exports.AiRunTargetTypeSchema = zod_1.z.enum(exports.AI_RUN_TARGET_TYPES);
/** One vendor call within a run — the default model or a fallback, success or failure. */
exports.AiRunAttemptSchema = zod_1.z.object({
    step: zod_1.z.string(),
    providerId: zod_1.z.string(),
    modelId: zod_1.z.string(),
    modelVersion: zod_1.z.string().optional(),
    unit: zod_1.z.enum(['input_token', 'output_token', 'image', 'second', 'call', 'mixed']),
    quantity: zod_1.z.number().nonnegative(),
    usage: zod_1.z.record(zod_1.z.number()).default({}),
    unitPriceUsd: zod_1.z.number().nonnegative(),
    costUsd: zod_1.z.number().nonnegative(),
    ok: zod_1.z.boolean(),
    error: zod_1.z.string().optional(),
    latencyMs: zod_1.z.number().int().nonnegative().default(0),
    at: zod_1.z.string(),
});
/** Run inputs an action needs beyond the prompt (video length, voice script, …). */
exports.AiRunParamsSchema = zod_1.z.record(zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.boolean()]));
/**
 * Where a run was started (follow-up 2026-10-05): `surface` is a human label
 * ("Menu Hub · Units", "Creator Studio · Word art"), `path` the app route
 * (hash route, no query). Callers cap the lengths on write.
 */
exports.AiRunSourceSchema = zod_1.z.object({
    surface: zod_1.z.string(),
    path: zod_1.z.string().optional(),
});
/** Write-side caps for a run's source (create-run / inline runs validate against these). */
exports.AI_RUN_SOURCE_MAX = { surface: 120, path: 300, locationId: 120 };
exports.AiRunRowSchema = zod_1.z.object({
    pk: zod_1.z.string().regex(/^TENANT#/),
    sk: zod_1.z.string().regex(/^RUN#/),
    runId: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().min(1),
    actorType: zod_1.z.enum(['tenant', 'admin_test', 'platform']),
    actorId: zod_1.z.string().default(''),
    actorEmail: zod_1.z.string().optional(),
    actionId: zod_1.z.string().min(1),
    vibeId: zod_1.z.string().nullable().optional(),
    target: zod_1.z.object({ type: zod_1.z.string(), id: zod_1.z.string(), name: zod_1.z.string().optional() }),
    /** Idempotency key — same click/retry returns the same run (one hold). */
    clientKey: zod_1.z.string().min(1),
    status: exports.AiRunStatusSchema,
    settlement: exports.AiRunSettlementSchema,
    promptFinal: zod_1.z.string().optional(),
    promptParts: zod_1.z.record(zod_1.z.string()).optional(),
    providerId: zod_1.z.string().optional(),
    modelId: zod_1.z.string().optional(),
    modelVersion: zod_1.z.string().optional(),
    usedFallback: zod_1.z.boolean().default(false),
    /** Append-only — every vendor attempt across every step. */
    attempts: zod_1.z.array(exports.AiRunAttemptSchema).default([]),
    vendorCostUsd: zod_1.z.number().nonnegative().default(0),
    multiplier: zod_1.z.number().nonnegative(),
    multiplierSource: zod_1.z.string(),
    rubyExact: zod_1.z.number().nonnegative().default(0),
    tokensReserved: zod_1.z.number().int().nonnegative().default(0),
    tokensCharged: zod_1.z.number().int().nonnegative().default(0),
    tokensRefunded: zod_1.z.number().int().nonnegative().default(0),
    chargedUsd: zod_1.z.number().nonnegative().default(0),
    profitUsd: zod_1.z.number().default(0),
    reservationId: zod_1.z.string().optional(),
    bucket: zod_1.z.string().optional(),
    allowanceCredits: zod_1.z.number().optional(),
    purchasedCredits: zod_1.z.number().optional(),
    free: zod_1.z.boolean().default(false),
    keepScene: zod_1.z.boolean().optional(),
    /** Picture runs: the merchant kept the drawn background (the 'background' step was skipped). */
    keepBackground: zod_1.z.boolean().optional(),
    /** Where the merchant started the run (additive, optional). */
    source: exports.AiRunSourceSchema.optional(),
    /** The merchant's selected location when the run started (additive, optional). */
    locationId: zod_1.z.string().optional(),
    output: zod_1.z.object({
        s3Key: zod_1.z.string(), url: zod_1.z.string(), mime: zod_1.z.string(),
        width: zod_1.z.number(), height: zod_1.z.number(), bytes: zod_1.z.number(),
        /** Video/voice runs: length of the delivered clip in seconds. */
        durationSec: zod_1.z.number().nonnegative().optional(),
        /** Video runs: a still frame of the clip, when one could be made. */
        thumbnailUrl: zod_1.z.string().optional(),
    }).optional(),
    /**
     * Action-specific run inputs fixed at create time (Task 26), e.g. a video's
     * durationSec / aspectRatio / voice-over script. Scalars only.
     */
    params: exports.AiRunParamsSchema.optional(),
    /**
     * The vendor long-running operation a video run is waiting on (Task 26).
     * Present only while the worker is polling it.
     */
    operation: zod_1.z.object({
        name: zod_1.z.string().min(1),
        providerId: zod_1.z.string(),
        modelId: zod_1.z.string(),
        step: zod_1.z.string(),
        /** Index of the step candidate that started it (a failed op tries the next). */
        candidateIndex: zod_1.z.number().int().nonnegative(),
        startedAt: zod_1.z.string(),
    }).optional(),
    /** Highest poll attempt the worker has claimed — a duplicate/stale poll message is dropped (Task 26). */
    pollAttempt: zod_1.z.number().int().nonnegative().optional(),
    error: zod_1.z.object({
        step: zod_1.z.string(), code: zod_1.z.string(), message: zod_1.z.string(), userMessage: zod_1.z.string(),
    }).optional(),
    feedback: zod_1.z.object({
        rating: zod_1.z.enum(['good', 'bad']), reason: zod_1.z.string().optional(),
        note: zod_1.z.string().max(500).optional(), at: zod_1.z.string(), by: zod_1.z.string(),
    }).optional(),
    cancelRequested: zod_1.z.boolean().optional(),
    /**
     * A wallet settle (commit/refund) that failed after the run's own settle
     * won — the sweeper retries it until it applies (ruling R20). Absent once
     * the wallet matches the run.
     */
    walletPending: zod_1.z.enum(['commit', 'refund']).optional(),
    /** Short, secret-free reason the pending wallet op last failed. */
    walletError: zod_1.z.string().optional(),
    createdAt: zod_1.z.string(),
    startedAt: zod_1.z.string().optional(),
    finishedAt: zod_1.z.string().optional(),
    durationMs: zod_1.z.number().int().optional(),
    /** Set the instant `settle` succeeds — doubles as the exactly-once guard (Task 7 fix round 1). */
    settledAt: zod_1.z.string().optional(),
    deadlineAt: zod_1.z.string(),
    occurredAt: zod_1.z.string(),
    /** GSI partition for the sweeper/reporting index, e.g. "RUN#2026-10". */
    gsiRunPk: zod_1.z.string(),
    /** Sparse GSI marker — present only while the run is open (not terminal). */
    gsiOpen: zod_1.z.literal('OPEN').optional(),
});
//# sourceMappingURL=ai-run.schema.js.map